import { useEffect } from "react"
import { getValidDateOrNull } from "utils/dateUtils"
import { randomIntFromInterval } from "utils/numberUtils"

/** Campaign dates are evaluated once, when the app loads. These are the
 * moments when what the shop shows changes (BF view, popups, prices). */
const BOUNDARY_ENV_DATES = [
  process?.env?.REACT_APP_CAMPAIGN_START_DATE,
  process?.env?.REACT_APP_CAMPAIGN_END_DATE,
  process?.env?.REACT_APP_BLACK_FRIDAY_POPUP_START_DATE,
  process?.env?.REACT_APP_BLACK_FRIDAY_LAST_MINUTE_POPUP_START_DATE,
  process?.env?.REACT_APP_BLACK_FRIDAY_LAST_MINUTE_POPUP_END_DATE,
]

// setTimeout cannot wait longer than ~24.8 days
const MAX_TIMEOUT_MS = 2147483647

export const getNextCampaignBoundary = (now = new Date(), envDates = BOUNDARY_ENV_DATES) =>
  envDates
    .map(getValidDateOrNull)
    .filter((date) => date && date > now)
    .sort((a, b) => a - b)[0] || null

/**
 * Reloads an open tab right after the next campaign boundary (1-20 s
 * random delay, like the Firebase forced refresh), so nobody keeps the
 * Black Friday view after 20:00 or misses it at 08:00 without reloading.
 * A hidden tab reloads when it becomes visible again.
 */
export const useReloadAtCampaignBoundaries = () => {
  useEffect(() => {
    const boundary = getNextCampaignBoundary()
    if (!boundary) return

    const delay = boundary - new Date() + randomIntFromInterval(1000, 20000)
    if (delay > MAX_TIMEOUT_MS) return

    const reload = () => window.location.reload()
    const reloadWhenVisible = () => {
      if (!document.hidden) reload()
    }

    const timer = setTimeout(() => {
      if (!document.hidden) reload()
      else document.addEventListener("visibilitychange", reloadWhenVisible)
    }, delay)

    return () => {
      clearTimeout(timer)
      document.removeEventListener("visibilitychange", reloadWhenVisible)
    }
  }, [])
}
